import React, { useState } from 'react';
import { array, arrayOf, bool, func, shape, string, oneOf, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { convertMoneyToNumber } from '../../util/currency';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';

import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSideNavigation,
  Modal,
  PrimaryButton,
} from '../../components';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  setInitialValues,
  fetchTransactionLineItems,
  fetchTimeSlots,
  sendInquiry,
} from './ListingPage.duck';
import MainTabNavVertical from '../../components/MainTabNavVertical/MainTabNavVertical';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionGallery from './SectionGallery';

import {
  // facebookIcon,
  // instagramIcon,
  linkedinIcon,
  // pinterestIcon,
  // tiktokIcon,
  twitterIcon,
  youtubeIcon,
} from './Icons';

import css from './ListingPage.module.css';
import { getCreditsAmont, getIsStudent } from '../../util/userUtil.js';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = ({
  isAuthenticated,
  currentUser = null,
  getListing,
  getOwnListing,
  intl,
  onManageDisableScrolling,
  onSendInquiry,
  onInitializeCardPaymentData,
  navigate,
  callSetInitialValues,
  params: rawParams,
  location,
  scrollingDisabled,
  showListingError = null,
  sendInquiryInProgress,
  sendInquiryError = null,
  monthlyTimeSlots = null,
  onFetchTimeSlots,
  // Line item estimation props
  lineItems = null,
  fetchLineItemsInProgress,
  fetchLineItemsError = null,
  onFetchTransactionLineItems,
  listingConfig: listingConfigProp = null,
  config,
  routeConfiguration,
  inquiryModalOpenForListingId = null,
  reviews: _reviews = [],
  fetchReviewsError: _fetchReviewsError = null,
}) => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    inquiryModalOpenForListingId === rawParams.id
  );
  const [orderPanelOpen, setOrderPanelOpen] = useState(false);

  // prop override makes testing a bit easier
  // TODO: improve this when updating test setup
  const listingConfig = listingConfigProp || config.listing;

  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the profiles from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the profiles
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const { description = '', price = null, title = '', publicData = {} } = currentListing.attributes;

  const { bio, experience, expertise, headline, listingType, services, cityOrRegion } = publicData;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const richBio = bio ? (
    <span
      dangerouslySetInnerHTML={{
        __html: bio.replace(/\n/g, '<br>'),
      }}
    />
  ) : null;

  const richServices = services ? (
    <span
      dangerouslySetInnerHTML={{
        __html: services.replace(/\n/g, '<br>'),
      }}
    />
  ) : null;

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);

  // When user is banned or deleted the profiles is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, navigate, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const schemaPriceMaybe = price
    ? {
        price: intl.formatNumber(convertMoneyToNumber(price), {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        priceCurrency: price.currency,
      }
    : {};
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability =
    currentStock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';

  const Labels = () => {
    const findLabels = (expertise, options) =>
      expertise.flatMap(exp =>
        options.filter(({ option }) => option === exp).map(({ label }) => label)
      );

    const expertiseLabels =
      expertise &&
      expertise.length > 0 &&
      listingConfig?.listingFields?.flatMap(({ enumOptions }) =>
        findLabels(expertise, enumOptions)
      );

    return (
      <div className={css.tagsContainer}>
        {expertiseLabels &&
          expertiseLabels.map((label, index) => (
            <span className={css.tag} key={index}>
              {label}
            </span>
          ))}
      </div>
    );
  };

  const isStudent = getIsStudent(currentUser);
  const userCredits = getCreditsAmont(currentUser);

  // const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label }));

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...schemaPriceMaybe,
          availability: schemaAvailability,
        },
      }}
    >
      <LayoutSideNavigation
        sideNavClassName={css.navigation}
        topbar={topbar}
        footer={<FooterContainer />}
        sideNav={<MainTabNavVertical intl={intl} currentUser={currentUser} />}
      >
        <div
          className={css.contentWrapperForProductLayout}
          style={{ padding: '0 36px', margin: '0 auto' }}
        >
          Mentor
        </div>
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainColumnForProductLayout}>
            {currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
            ) : null}
            <SectionGallery
              listing={currentListing}
              variantPrefix={config.layout.listingImage.variantPrefix}
              aspectWidth={config.layout.listingImage.aspectWidth || 1}
              aspectHeight={config.layout.listingImage.aspectHeight || 1}
            />
            <div className={css.mobileHeading}>
              <H4 as="h1" className={css.orderPanelTitle}>
                <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
              </H4>
            </div>

            <div className={css.infoContainer}>
              <div className={css.rowSpaceBetween}>
                <H4 as="h1" className={css.name}>
                  {richTitle}
                </H4>
                <div className={css.socialMediaIcons}>
                  {youtubeIcon()}
                  {linkedinIcon()}
                  {twitterIcon()}
                </div>
              </div>

              <div>
                <p className={css.headline}>{headline}</p>
                <p>{richBio}</p>
              </div>
              <div>
                <Labels />

                {isStudent ? (
                  userCredits === 0 ? (
                    <p>
                      <FormattedMessage id="ListingPage.notEnoughCredits" />
                    </p>
                  ) : (
                    <div className={css.buttonWrapper}>
                      <SectionAuthorMaybe
                        showOnlyContact={true}
                        title={title}
                        listing={currentListing}
                        authorDisplayName={authorDisplayName}
                        onContactUser={onContactUser}
                        isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                        onCloseInquiryModal={() => setInquiryModalOpen(false)}
                        sendInquiryError={sendInquiryError}
                        sendInquiryInProgress={sendInquiryInProgress}
                        onSubmitInquiry={onSubmitInquiry}
                        currentUser={currentUser}
                        onManageDisableScrolling={onManageDisableScrolling}
                      />

                      <PrimaryButton
                        className={css.ctaButton}
                        onClick={() => setOrderPanelOpen(true)}
                      >
                        <FormattedMessage id={`ListingPage.ctaButton.${listingType}`} />
                      </PrimaryButton>
                    </div>
                  )
                ) : (
                  <>
                    <p className="error">
                      <FormattedMessage id="ListingPage.mentorCannotBook" />
                    </p>

                    {/* <SectionAuthorMaybe
                      showOnlyContact={true}
                      title={title}
                      listing={currentListing}
                      authorDisplayName={authorDisplayName}
                      onContactUser={onContactUser}
                      isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                      onCloseInquiryModal={() => setInquiryModalOpen(false)}
                      sendInquiryError={sendInquiryError}
                      sendInquiryInProgress={sendInquiryInProgress}
                      onSubmitInquiry={onSubmitInquiry}
                      currentUser={currentUser}
                      onManageDisableScrolling={onManageDisableScrolling}
                    /> */}
                  </>
                )}
              </div>
            </div>

            {/* <SectionTextMaybe text={description} showAsIngress />
            <SectionDetailsMaybe
              publicData={publicData}
              metadata={metadata}
              listingConfig={listingConfig}
              intl={intl}
            />
            {listingConfig.listingFields.reduce((pickedElements, config) => {
              const { key, enumOptions, includeForListingTypes, scope = 'public' } = config;
              const listingType = publicData?.listingType;
              const isTargetListingType =
                includeForListingTypes == null || includeForListingTypes.includes(listingType);

              const value =
                scope === 'public' ? publicData[key] : scope === 'metadata' ? metadata[key] : null;
              const hasValue = value != null;
              return isTargetListingType && config.schemaType === SCHEMA_TYPE_MULTI_ENUM
                ? [
                  ...pickedElements,
                  <SectionMultiEnumMaybe
                    key={key}
                    heading={config?.showConfig?.label}
                    options={createFilterOptions(enumOptions)}
                    selectedOptions={value || []}
                  />,
                ]
                : isTargetListingType && hasValue && config.schemaType === SCHEMA_TYPE_TEXT
                  ? [
                    ...pickedElements,
                    <SectionTextMaybe key={key} heading={config?.showConfig?.label} text={value} />,
                  ]
                  : pickedElements;
            }, [])}

            <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
            <SectionAuthorMaybe
              title={title}
              listing={currentListing}
              authorDisplayName={authorDisplayName}
              onContactUser={onContactUser}
              isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
              onCloseInquiryModal={() => setInquiryModalOpen(false)}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              currentUser={currentUser}
              onManageDisableScrolling={onManageDisableScrolling}
            /> */}
          </div>
          <div className={css.orderColumnForProductLayout}>
            <div>
              <label className={css.label}>City/Region</label>
              <p className={css.info}>{cityOrRegion || 'Not provided'}</p>
            </div>

            <div>
              <label className={css.label}>Experience</label>
              {/* <p className={css.info}>{headline}</p> */}
              {experience.map((exp, index) => (
                <p className={css.info} key={index}>
                  {exp}
                </p>
              ))}
            </div>

            <div>
              <label className={css.label}>What can mentees expect from me?</label>
              {richServices}
            </div>
          </div>
        </div>
      </LayoutSideNavigation>

      <Modal
        id="ListingPage.OrderPanel"
        isOpen={orderPanelOpen}
        onClose={() => setOrderPanelOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        scrollLayerClassName={css.scrollLayer}
      >
        <OrderPanel
          className={css.productOrderPanel}
          listing={currentListing}
          currentUser={currentUser}
          isOwnListing={isOwnListing}
          onSubmit={handleOrderSubmit}
          scrollLayerClassName={css.scrollLayer}
          containerClassName={css.modalContainer}
          navigate={navigate}
          location={location}
          authorLink={
            <NamedLink
              className={css.authorNameLink}
              name="ListingPage"
              params={params}
              to={{ hash: '#author' }}
            >
              {authorDisplayName}
            </NamedLink>
          }
          title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
          titleDesktop={
            <H4 as="h1" className={css.orderPanelTitle}>
              <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
            </H4>
          }
          author={ensuredAuthor}
          onManageDisableScrolling={onManageDisableScrolling}
          onContactUser={onContactUser}
          monthlyTimeSlots={monthlyTimeSlots}
          onFetchTimeSlots={onFetchTimeSlots}
          onFetchTransactionLineItems={onFetchTransactionLineItems}
          lineItems={lineItems}
          fetchLineItemsInProgress={fetchLineItemsInProgress}
          fetchLineItemsError={fetchLineItemsError}
          validListingTypes={config.listing.listingTypes}
          marketplaceCurrency={config.currency}
          dayCountAvailableForBooking={config.dayCountAvailableForBooking}
          marketplaceName={config.marketplaceName}
        />
      </Modal>
    </Page>
  );
};



ListingPageComponent.propTypes = {
  // from useNavigate
  navigate: func.isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  listingConfig: object,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      navigate={navigate}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  // Sonispark uses credits-based system, not real payments
  onInitializeCardPaymentData: () => {},
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
